import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';

import ModalConfirmation from 'components/Modal/ConfirmationModal';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';

import contactService from '../../../../../services/contactService';
import { toast } from 'react-toastify';

import './styles.css';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import { VoiceProtocol } from 'views/Clients/list/components/VoiceProtocol';
import HistoryConversation from 'views/ServicePanel/components/AttendanceContent/components/Modal/HistoryConversation';
import { ClientProtocol } from 'views/Clients/list/components/ClientProtocol';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';

import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import FlexTable from 'components/FlexTable';
import { TagChatClient } from 'components/TagChatClient';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const ListContactsTable = ({
  contacts,
  reloadContacts,
  customFields,
  setSortConfig,
  sortConfig
}) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [contactId, setContactId] = useState(null);
  const history = useHistory();
  const { recordTypes, getRecordTypesByUser } = useRecordTypesByUser(
    entitiesName.PROTOCOL
  );

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  const handleDeleteLead = useCallback(
    (id) => {
      contactService
        .deleteContact(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Contato deletado com sucesso';
          toast.error(message);
        })
        .finally(() => {
          setOpenModalConfirmation(false);
          reloadContacts(1);
        });
    },
    [reloadContacts]
  );

  const editContact = (contact) => {
    history.push(
      `/admin/crm/contactForm/${contact.recordTypeId}/${contact.id}`
    );
  };

  const controlDeleteContact = (id) => {
    setContactId(id);
    setOpenModalConfirmation(true);
  };

  const createDynamicColumns = () => {
    const columns = [];

    columns.push(
      {
        dataField: 'nome',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_CONTACTS_EDIT']) ? (
                <span
                  className="contact-name"
                  onClick={() => {
                    editContact(row);
                  }}
                >
                  {row.name}
                </span>
              ) : (
                <span>{row.name}</span>
              )}
            </>
          );
        }
      },
      {
        dataField: 'telefone',
        text: 'Telefone',
        style: individual_column_style,
        formatter: (_, row) => {
          if (!row?.phone) return null;
          return <PhoneCell phone={row.phone} ddi={row?.ddi} />;
        }
      },
      {
        dataField: 'created_at',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, cell) => {
          if (cell.createdAt) return toBrazilianFullDate(cell.createdAt);
        }
      },
      {
        dataField: 'created_by',
        text: 'Criado por',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { createdByName }) => createdByName
      },
      {
        dataField: 'last_updated_at',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, cell) => {
          if (cell.lastUpdatedAt)
            return toBrazilianFullDate(cell.lastUpdatedAt);
        }
      },
      {
        dataField: 'associatedleads',
        text: 'Lead Associado',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associatedleads }) => associatedleads
      },
      {
        dataField: 'associatedopportunities',
        text: 'Oportunidade Associada',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associatedopportunities }) =>
          associatedopportunities
      }
    );

    columns.push({
      dataField: 'tags',
      text: 'Tags do Cliente',
      sort: true,
      style: individual_column_style,
      headerStyle: individual_header_style,
      formatter: (cell) => {
        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              maxWidth: '13.75rem',
              width: '13.75rem',
              wordWrap: 'break-word',
              paddingTop: '0.6rem',
              backgroundColor: '#fff'
            }}
          >
            {cell?.length > 0 &&
              cell.map((tag, index) => {
                return (
                  <TagChatClient
                    key={index}
                    name={tag?.name}
                    isMore={false}
                    color={tag?.color}
                    disabled={false}
                    index={index}
                    sizeText="auto"
                    inPopover={true}
                  />
                );
              })}
          </div>
        );
      }
    });

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        style: individual_column_style,
        formatter: (_, row) => {
          if (field.type === 'DATE' && !!row[field.name]) {
            return new Date(row[field.name]).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && row[field.name]) {
            return (
              <PhoneCell
                phone={row[field?.name].number}
                ddi={row[field?.name]?.ddi}
              />
            );
          } else if (field.type === 'CPF' && row[field.name]) {
            return maskCpf(row[field.name]);
          } else if (field.type === 'CNPJ' && row[field.name]) {
            return maskCnpj(row[field.name]);
          } else if (field.type === 'CURRENCY' && row[field.name]) {
            return formatCurrency(row[field.name]);
          }
          return row[field.name];
        }
      });
    });

    columns.push({
      dataField: 'edit',
      text: 'Editar',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {verifyRoles(['CRM_CONTACTS_EDIT']) ? (
              <Button size="sm" color="info" onClick={() => editContact(row)}>
                <li className="fas fa-user-edit"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    columns.push({
      dataField: 'voice',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      text: 'Voz',
      formatter: (_, row) => {
        return (
          <VoiceProtocol
            disabled={row.phone ? false : true}
            telefone={row.phone}
          />
        );
      }
    });

    columns.push({
      dataField: 'chat',
      style: {
        ...icon_column_style,
        display: 'flex',
        justifyContent: 'center'
      },
      headerStyle: individual_icon_header_style,
      text: 'Chat',
      formatter: (_, row) => {
        return (
          <HistoryConversation
            name="Histórico de atendimentos"
            tooltipTitle="Histórico"
            id={row.id}
            isButton={true}
            colorButton="success"
            newIcon={true}
          />
        );
      }
    });

    columns.push({
      dataField: 'protocol',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      text: 'Protocolos',
      formatter: (_, row) => {
        return (
          <ClientProtocol
            recordTypes={recordTypes}
            clientInfo={row.nome}
            clientId={row.id}
          />
        );
      }
    });

    columns.push({
      dataField: 'delete',
      text: 'Excluir',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['CRM_CONTACTS_DELETE']) ? (
              <Button
                size="sm"
                color="danger"
                onClick={() => controlDeleteContact(row.id)}
              >
                <li className="fas fa-trash"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    return columns;
  };

  const dynamicFunctionColumns = createDynamicColumns();

  return (
    <>
      <ToolkitProvider
        data={contacts || []}
        keyField="id"
        columns={dynamicFunctionColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            <FlexTable
              {...props.baseProps}
              classes="align-items-center"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
              entityName="contact"
            />
          </div>
        )}
      </ToolkitProvider>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={contactId}
        deleteResource={handleDeleteLead}
        message={'Tem certeza que deseja excluir o contato?'}
      />
    </>
  );
};

export default ListContactsTable;
